
/**
 * @fileoverview Componente atom CustomerInfo para mostrar información básica del cliente (nombre, teléfono y bandera país).
 * Presenta datos esenciales del cliente en formato compacto y optimizado, siguiendo Atomic Design (nivel átomo).
 * Incluye optimización de renderizado con React.memo y uso de useMemo para performance.
 *
 * @module CustomerInfo
 * @category Component - Atom
 * @since 3.1.0
 * @version 3.1.1 - Documentación JSDoc completa, optimización de memoización y validaciones.
 */

/**
 * @typedef {Object} CustomerType
 * @description Estructura de datos del cliente para CustomerInfo.
 * @property {string} [fullName] - Nombre completo del cliente.
 * @property {string} [full_names] - Nombre completo alternativo.
 * @property {string} [first_name] - Primer nombre del cliente.
 * @property {string} [last_name] - Apellido del cliente.
 * @property {string} [phone_with_country_code] - Teléfono con código de país.
 * @property {string} [phone] - Teléfono sin código de país.
 * @property {string|number} [country_id] - ID del país para bandera.
 */

/**
 * Componente atom para mostrar información básica del cliente.
 *
 * @component CustomerInfo
 * @category Component - Atom
 *
 * @description
 * **Características:**
 * - Presenta nombre y teléfono del cliente con bandera del país
 * - Optimizado con React.memo y useMemo para evitar renders innecesarios
 * - Validación de datos mínimos requeridos
 * - Estilizado con TailwindCSS (tw-wrapper)
 *
 * **Atomic Design:**
 * - Nivel: Atom
 * - Composición: No depende de otros componentes, solo utilidades
 * - Dependencias: getFlagIcon (servicio)
 *
 * @param {Object} props - Propiedades del componente
 * @param {CustomerType} props.currentCustomer - Objeto con datos del cliente
 * @param {string} [props.className] - Clases CSS adicionales para el contenedor
 *
 * @returns {JSX.Element|null} Elemento renderizado con la información del cliente o null si no hay datos válidos
 *
 * @example
 * <CustomerInfo currentCustomer={{ fullName: 'Juan Pérez', phone: '+573001234567', country_id: 'CO' }} />
 *
 * @see {@link ../../../../services/countryService} Utilidad para obtener bandera de país
 *
 * @since 3.1.0
 * @version 3.1.1 - Documentación JSDoc completa y optimización
 */

import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getFlagIcon } from '../../../../services/countryService';
import { whatsappRecipientLabel } from '../../../../util/whatsappMessageUtils';


const CustomerInfo = memo(({ currentCustomer, className = "" }) => {
  const { t } = useTranslation('NewChat');


  /**
   * Memoiza y formatea la información del cliente para mostrar.
   *
   * @function formattedCustomerInfo
   * @memberof CustomerInfo
   * @private
   * @description
   * **Flujo de operación:**
   * 1. Verifica si existe currentCustomer
   * 2. Obtiene nombre a mostrar (fullName, full_names, o combinación de first_name y last_name)
   * 3. Obtiene teléfono (con o sin código de país)
   * 4. Obtiene bandera del país usando country_id
   * 5. Valida que haya al menos nombre o teléfono
   * 6. Retorna objeto formateado o null si no hay datos
   *
   * @param {CustomerType} currentCustomer - Datos del cliente
   * @returns {{displayName: string, phone: string, countryCode: string|null}|null} Objeto con datos formateados o null
   *
   * @example
   * // Ejemplo de retorno
   * {
   *   displayName: 'Juan Pérez',
   *   phone: '+573001234567',
   *   countryCode: 'CO'
   * }
   */
  const formattedCustomerInfo = useMemo(() => {
    if (!currentCustomer) return null;

    const displayName = currentCustomer.fullName || currentCustomer.full_names || `${currentCustomer.first_name || ''} ${currentCustomer.last_name || ''}`.trim();
    const recipientLabel = whatsappRecipientLabel(currentCustomer, {
      userId: t('customer.withUserId'),
      parentUserId: t('customer.withParentUserId')
    });
    const countryCode = currentCustomer.country_id || null;

    // Validación de datos mínimos requeridos
    if (!displayName && !recipientLabel) {
      return null;
    }

    return {
      displayName,
      phone: recipientLabel,
      countryCode
    };
  }, [currentCustomer, t]);


  /**
   * Validación temprana: retorna null si no hay información válida del cliente.
   * @memberof CustomerInfo
   */
  if (!formattedCustomerInfo) {
    return null;
  }

  const { displayName, phone, countryCode } = formattedCustomerInfo;


  /**
   * Renderiza la información del cliente con estilos TailwindCSS.
   * @memberof CustomerInfo
   * @returns {JSX.Element}
   */
  return (
    <div className={`tw-px-4 tw-py-3 tw-bg-gray-100 tw-rounded-lg tw-mb-6 ${className}`.trim()}>
      <div className="tw-font-semibold tw-text-gray-900">
        {displayName}
      </div>
      <div className="tw-text-sm tw-text-gray-600 tw-flex tw-items-center tw-mt-1">
        {getFlagIcon(countryCode, 'tw-mr-2')}
        <span>{phone}</span>
      </div>
    </div>
  );
});


/**
 * Nombre de display para el componente CustomerInfo.
 * @type {string}
 */
CustomerInfo.displayName = 'CustomerInfo';

export default CustomerInfo;
